import { backendClient } from '@/lib/api-client';

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * POST the markdown content to the backend `/document-export/{matter_guid}` endpoint
 * and trigger a browser download of the returned .docx. The backend handles citation
 * numbering, document title resolution, and styling via its reference doc.
 */
export async function exportMarkdownAsDocx(
  matterGuid: string,
  markdown: string,
  includeCitations: boolean,
  filename: string,
): Promise<void> {
  let response;
  try {
    response = await backendClient.post(
      `/document-export/${matterGuid}?include_citations=${includeCitations}`,
      { content: markdown, target_format: 'docx' },
      { responseType: 'arraybuffer' },
    );
  } catch (error: any) {
    // FastAPI errors are JSON `{detail: ...}` but arrive as an ArrayBuffer here because
    // we asked for `arraybuffer`. Try to decode; on any decode/parse failure fall through
    // to the original axios error rather than surfacing "Unexpected token …" to the user.
    const data = error?.response?.data;
    if (data instanceof ArrayBuffer) {
      try {
        const parsed = JSON.parse(new TextDecoder().decode(data));
        throw new Error(parsed.detail || parsed.message || 'Failed to export document');
      } catch (decodeError) {
        // SyntaxError means the body wasn't JSON (e.g. an HTML proxy error page) —
        // fall through to the original axios error. Anything else is the Error we
        // built above and should bubble up.
        if (!(decodeError instanceof SyntaxError)) {
          throw decodeError;
        }
      }
    }
    throw error;
  }

  const blob = new Blob([new Uint8Array(response.data)], { type: DOCX_MIME_TYPE });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Firefox historically required the anchor to be in the DOM for click() to fire
  // a download (Bug 1091035). Modern Firefox handles detached clicks fine, but
  // appending costs nothing and removes the failure mode entirely.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
